import { CarrierType, CarrierVisitDirection, OrderStatus } from '@planning/app/api'
import { GeneralCargoOrderLocationItem } from '@planning/pages/GeneralCargo/Components/GeneralCargoOrderLocationItem'
import { formatOrderLocations } from '@planning/pages/GeneralCargo/Components/GeneralCargoOrdersTable'
import { TFnType } from '@tolgee/react'
import { ColoredLabel } from '@tom-ui/ui'
import { MRT_ColumnDef } from 'material-react-table'
import { GeneralCargoOrderListTableColumnsDto } from '../Stores/VesselVisitOrderListViewStore'
import { columnFilterPresets } from './columnFilterConfigs'

export const getGeneralCargoListColumns = (
  t: TFnType,
  direction: CarrierVisitDirection,
  carrierVisitType?: CarrierType,
  orderLocationsById?: Record<number, GeneralCargoOrderLocationItem[]>,
  orderReservationsById?: Record<number, GeneralCargoOrderLocationItem[]>,
): MRT_ColumnDef<GeneralCargoOrderListTableColumnsDto>[] => {
  const columns: MRT_ColumnDef<GeneralCargoOrderListTableColumnsDto>[] = [
    {
      accessorKey: 'commodity',
      header: t('commodity', 'Commodity'),
      size: 150,
      ...columnFilterPresets.searchMandatory(),
    },
    {
      accessorKey: 'referenceNumber',
      header: t('referenceNo', 'Reference no.'),
      size: 150,
      ...columnFilterPresets.searchMandatory(),
    },
    {
      accessorKey: 'lotNumber',
      header: t('lotNo', 'LOT no.'),
      size: 150,
      ...columnFilterPresets.searchOptional(),
    },
    {
      accessorKey: 'status',
      header: t('status', 'Status'),
      size: 150,
      ...columnFilterPresets.openFulfilledSelect(),
      Cell: cell => (
        <ColoredLabel
          sx={{ display: 'inline-block' }}
          label={
            cell.row.original.status === OrderStatus.Open
              ? t('open', 'Open')
              : t('fulfilled', 'Fulfilled')
          }
          color={cell.row.original.status === OrderStatus.Open ? 'info' : 'success'}
        />
      ),
    },
  ]

  if (carrierVisitType === CarrierType.Train) {
    columns.push(
      {
        accessorKey: 'railTrackName',
        header: t('railTrackNo', 'Rail track no.'),
        size: 150,
        ...columnFilterPresets.searchOptional(),
      },
      {
        accessorKey: 'railcarName',
        header: t('railcarNo', 'Railcar no.'),
        size: 150,
        ...columnFilterPresets.searchOptional(),
      },
    )
  }

  columns.push(
    {
      accessorKey: 'customer',
      header: t('customer', 'Customer'),
      size: 150,
      ...columnFilterPresets.searchMandatory(),
    },
    {
      id: 'plannedLocation',
      accessorKey: 'plannedLocation',
      header: t('plannedLocation', 'Planned location'),
      size: 180,
      accessorFn: row => {
        return formatOrderLocations(orderReservationsById?.[row.id]) ?? ''
      },
      Cell: ({ cell }) => (cell.getValue() as string) || '-',
      ...columnFilterPresets.searchOptional(),
    },
  )

  if (direction === CarrierVisitDirection.Inbound) {
    columns.push({
      id: 'currentLocation',
      accessorKey: 'currentLocation',
      header: t('currentLocation', 'Current location'),
      size: 200,
      accessorFn: row => {
        return formatOrderLocations(orderLocationsById?.[row.id]) ?? ''
      },
      Cell: ({ cell }) => (cell.getValue() as string) || '-',
      ...columnFilterPresets.searchOptional(),
    })
  }

  columns.push(
    {
      accessorKey: 'plannedQuantity',
      header: t('plannedQuantity', 'Planned quantity'),
      size: 200,
      ...columnFilterPresets.numericMandatory(),
    },
    {
      accessorKey: 'actualQuantity',
      header: t('actualQuantity', 'Actual quantity'),
      size: 150,
      ...columnFilterPresets.numericOptional(),
    },
    {
      accessorKey: 'weight',
      header: t('weight', 'Weight'),
      size: 150,
      ...columnFilterPresets.numericMandatory(),
    },
    {
      accessorKey: 'weightUnit',
      header: t('unit', 'Unit'),
      size: 150,
      ...columnFilterPresets.searchMandatory(),
    },
    {
      accessorKey: 'package',
      header: t('package', 'Package'),
      size: 150,
      ...columnFilterPresets.searchOptional(),
    },
    {
      accessorKey: 'imoClass',
      header: t('imoClass', 'IMO class'),
      size: 150,
      ...columnFilterPresets.arrayContainsValue(),
    },
  )

  if (carrierVisitType === CarrierType.Vessel) {
    columns.push({
      accessorKey: 'category',
      accessorFn: row =>
        direction === CarrierVisitDirection.Inbound ? t('import', 'Import') : t('export', 'Export'),
      header: t('category', 'Category'),
      size: 150,
      ...columnFilterPresets.searchMandatory(),
    })
  }

  return columns
}
